import { Injectable } from '@angular/core';
import {
  HttpBackend,
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
  HttpResponse,
  HttpUrlEncodingCodec
} from '@angular/common/http';
import { Observable } from 'rxjs';

import { Configuration } from '@bo-client/core/configuration';
import { SearchSortDtoModel } from '@bo-client/shared/dynamic-search-serverside/dynamic-search.model';
import { ModelPage } from '@bo-client/shared/dynamic-search-serverside/model-page';
import { ConfigService } from '@bo-client/config/config.service';
import { ActivityDto } from '@bo-client/shared/models/activityDto';
import { SKIP_FORBIDDEN_TOAST } from '@bo-client/core/http/http-context-tokens';

@Injectable({
  providedIn: 'root'
})
export class HttpHapService {
  public defaultHeaders = new HttpHeaders();
  public configuration = new Configuration();

  // Disable Interceptors
  public httpClientback: HttpClient;

  constructor(
    private http: HttpClient,
    private httpBackend: HttpBackend,
    private configService: ConfigService
  ) {
    this.httpClientback = new HttpClient(httpBackend);
  }

  get<T = any>(apiUri: string): Observable<T> {
    return this.http.get<T>(`${this.configService.getSettings('serverUrl')}${apiUri}`);
  }

  get2(apiUri: string, options: any): Observable<any> {
    return this.http.get<any>(`${this.configService.getSettings('serverUrl')}${apiUri}`, options);
  }

  getTyped<T = any>(apiUri: string): Observable<T> {
    return this.http.get<T>(`${this.configService.getSettings('serverUrl')}${apiUri}`);
  }

  getBasedOnBECredentials(apiUri: string): Observable<any> {
    return this.http.get<any>(`${this.configService.getSettings('serverUrl')}${apiUri}`);
  }

  create(entity: any, apiUri: string): Observable<any> {
    return this.http.post<any>(`${this.configService.getSettings('serverUrl')}${apiUri}`, entity);
  }

  findByObject<TResponse = any, TRequest = any>(entity: TRequest, apiUri: string): Observable<TResponse> {
    return this.http.post<TResponse>(`${this.configService.getSettings('serverUrl')}${apiUri}`, entity);
  }

  putOrPost<TResponse = any>(body: any, apiUri: string): Observable<TResponse> {
    const method = body.id ? 'PUT' : 'POST';
    return this.http.request<TResponse>(method, `${this.configService.getSettings('serverUrl')}${apiUri}`, {
      body: body
    });
  }

  put<TResponse = any>(body: any, apiUri: string): Observable<TResponse> {
    const method = 'PUT';
    return this.http.request<TResponse>(method, `${this.configService.getSettings('serverUrl')}${apiUri}`, {
      body: body
    });
  }

  /**
   * @param context optionaler `HttpContext` für die Interceptoren, z. B. `SKIP_ERROR_TOAST`
   *   (`core/http/http-context-tokens.ts`), wenn der Aufrufer Fehler selbst anzeigt
   */
  post<TResponse = any>(body: any, apiUri: string, context?: HttpContext): Observable<TResponse> {
    const method = 'POST';
    return this.http.request<TResponse>(method, `${this.configService.getSettings('serverUrl')}${apiUri}`, {
      body: body,
      context
    });
  }
  postTyped<T>(body: any, apiUri: string): Observable<T> {
    const method = 'POST';
    return this.http.request<T>(method, `${this.configService.getSettings('serverUrl')}${apiUri}`, {
      body: body
    });
  }

  postBlobResponse(body: any, apiUri: string): Observable<HttpResponse<Blob>> {
    return this.http.post(`${this.configService.getSettings('serverUrl')}${apiUri}`, body, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  delete<TResponse = any>(apiUri: string): Observable<TResponse> {
    return this.http.delete<TResponse>(`${this.configService.getSettings('serverUrl')}${apiUri}`);
  }

  update(entity: any, apiUri: string): Observable<any> {
    return this.http.post<any>(`${this.configService.getSettings('serverUrl')}${apiUri}`, entity);
  }

  deleteByEntity(entity: any, apiUri: string): Observable<any> {
    return this.http.post<any>(`${this.configService.getSettings('serverUrl')}${apiUri}`, entity);
  }

  getAllPage(page: ModelPage, apiUri: string): Observable<any> {
    let params = new HttpParams({ encoder: new HttpUrlEncodingCodec() });
    params = params.set('page', page.pageNumber.toString());
    params = params.set('size', page.pageSize.toString());
    return this.http.get<any>(`${this.configService.getSettings('serverUrl')}${apiUri}`, { params });
  }

  getAllPageWithFilter<TResponse = any>(
    page: ModelPage,
    searchSortDtoModel: SearchSortDtoModel,
    apiUri: string
  ): Observable<TResponse> {
    let params = new HttpParams({ encoder: new HttpUrlEncodingCodec() });
    params = params.set('page', page.pageNumber.toString());
    params = params.set('size', page.pageSize.toString());
    return this.http.post<TResponse>(
      `${this.configService.getSettings('serverUrl')}${apiUri}`,
      searchSortDtoModel,
      {
        params
      }
    );
  }

  getPdf(apiUri: string): Observable<any> {
    const httpOptions = {
      responseType: 'arraybuffer' as 'json'
    };
    return this.http.get<any>(`${this.configService.getSettings('serverUrl')}${apiUri}`, httpOptions);
  }

  /**
   * @param context optionaler `HttpContext` für die Interceptoren, z. B. `SKIP_ERROR_TOAST`, wenn der
   *   Aufrufer Fehler selbst anzeigt (Mail-Template-Editor)
   */
  postPdf(body: any, apiUri: string, context?: HttpContext): Observable<any> {
    const httpOptions = {
      responseType: 'arraybuffer' as 'json',
      context
    };
    return this.http.post(`${this.configService.getSettings('serverUrl')}${apiUri}`, body, httpOptions);
  }

  /** Lädt die Aktivitäten eines Antrags; eine fehlende Berechtigung (403) erzeugt keinen Toast. */
  getActivitiesByReferenceId(referenceId: string) {
    // Todo: URL anpassen, wenn im backend behoben
    return this.http.get<ActivityDto[]>(
      `${this.configService.getSettings('serverUrl')}/activites/${referenceId}`,
      { context: new HttpContext().set(SKIP_FORBIDDEN_TOAST, true) }
    );
  }
}
